import { z } from "zod/v4";

import { OrgShopIdDtoSchema } from "../../../dtos/organization-shop-id";
import {
  StripeApiCouponDtoSchema,
  StripeApiCreateCouponDtoSchema,
  StripeApiCreateSubscriptionSettingDtoSchema,
  StripeApiCustomerDtoSchema,
  StripeApiPriceDtoSchema,
  StripeApiProductDtoSchema,
  StripeApiProductFeatureDtoSchema,
  StripeApiSubscriptionDtoSchema,
  StripeApiSubscriptionLinkDtoSchema,
} from "../../../dtos/stripe-api";
import { StripeSubscriptionLinkDtoSchema } from "../../../dtos/stripe-subscription-links";
import {
  SubscriptionBillingProviderList,
  SubscriptionProrationBehaviorList,
} from "../../../dtos/subscriptions";
import { StripeProductVersionList } from "../../../schemas/stripe";

export const StripeApiActionList = [
  "getAllCustomers",
  "getAllProducts",
  "getPricesByProductId",
  "getAllProductFeaturesByProductId",
  "createCustomer",
  "createSubscription",
] as const;
export type StripeApiAction = (typeof StripeApiActionList)[number];

export const StripeApiSubscriptionLineItemSchema = z.object({
  id: z.string().optional(),
  priceId: z.string(),
  quantity: z.number(),
  defaultQuantity: z.number().nullish(),
  deleted: z.boolean().optional(),
  version: z.enum(StripeProductVersionList),
});

export type StripeApiSubscriptionLineItemType = z.infer<
  typeof StripeApiSubscriptionLineItemSchema
>;
export const StripeApiGetAllCustomersResponseBodySchema = z.object({
  action: z.literal("getAllCustomers"),
  customers: StripeApiCustomerDtoSchema.array(),
});

export type StripeApiGetAllCustomersResponseBodyType = z.infer<
  typeof StripeApiGetAllCustomersResponseBodySchema
>;

export const StripeApiGetAllProductsResponseBodySchema = z.object({
  action: z.literal("getAllProducts"),
  products: StripeApiProductDtoSchema.array(),
});

export type StripeApiGetAllProductsResponseBodyType = z.infer<
  typeof StripeApiGetAllProductsResponseBodySchema
>;

export const StripeApiGetSubscriptionByIdResponseBodySchema = z.object({
  action: z.literal("getSubscriptionById"),
  subscription: StripeApiSubscriptionDtoSchema,
});

export type StripeApiGetSubscriptionByIdResponseBodyType = z.infer<
  typeof StripeApiGetSubscriptionByIdResponseBodySchema
>;

export const StripeApiGetAllPricesByProductIdResponseBodySchema = z.object({
  action: z.literal("getPricesByProductId"),
  prices: StripeApiPriceDtoSchema.array(),
});

export const StripeApiCreateSubscriptionResponseBodySchema = z.object({
  action: z.literal("createSubscription"),
  subscription: z.object({
    id: z.string(),
  }),
});

export type StripeApiCreateSubscriptionResponseBodyType = z.infer<
  typeof StripeApiCreateSubscriptionResponseBodySchema
>;

export const StripeApiGetAllProductFeaturesByProductIdResponseBodySchema =
  z.object({
    action: z.literal("getAllProductFeaturesByProductId"),
    features: StripeApiProductFeatureDtoSchema.array(),
  });

export type StripeApiGetAllProductFeaturesByProductIdResponseBodyType = z.infer<
  typeof StripeApiGetAllProductFeaturesByProductIdResponseBodySchema
>;

export type StripeApiGetAllPricesByProductIdResponseBodyType = z.infer<
  typeof StripeApiGetAllPricesByProductIdResponseBodySchema
>;

export type StripeApiResponseBodyType = z.infer<
  typeof StripeApiResponseBodySchema
>;

export const StripeApiGetAllCustomersActionSchema = z.object({
  action: z.literal("getAllCustomers"),
  orgs: OrgShopIdDtoSchema.array(),
});

export const StripeApiCreateSubscriptionSchedulePhaseDtoSchema = z.object({
  phaseStart: z.number(),
  phaseEnd: z.number().nullish(),
  items: StripeApiSubscriptionLineItemSchema.array(),
  couponId: z.string().nullish(),
  trialEndTs: z.number().nullish(),
});

export type StripeApiCreateSubscriptionSchedulePhaseDtoType = z.infer<
  typeof StripeApiCreateSubscriptionSchedulePhaseDtoSchema
>;

export const StripeApiCreateSubscriptionActionSchema = z.object({
  orgs: OrgShopIdDtoSchema.array(),
  action: z.literal("createSubscription"),
  customerId: z.string(),
  stripeSubscriptionLink: StripeSubscriptionLinkDtoSchema.nullish(),
  billingProvider: z.enum(SubscriptionBillingProviderList),
  phases: StripeApiCreateSubscriptionSchedulePhaseDtoSchema.array(),
  settings: StripeApiCreateSubscriptionSettingDtoSchema,
});

export type StripeApiCreateSubscriptionActionType = z.infer<
  typeof StripeApiCreateSubscriptionActionSchema
>;

export type StripeApiGetAllCustomersActionType = z.infer<
  typeof StripeApiGetAllCustomersActionSchema
>;

export const StripeApiGetAllProductsActionSchema = z.object({
  action: z.literal("getAllProducts"),
  orgs: OrgShopIdDtoSchema.array(),
});

export type StripeApiGetAllProductsActionType = z.infer<
  typeof StripeApiGetAllProductsActionSchema
>;

export const StripeApiGetAllProductFeaturesByProductIdActionSchema = z.object({
  action: z.literal("getAllProductFeaturesByProductId"),
  orgs: OrgShopIdDtoSchema.array(),
  productId: z.string(),
});

export type StripeApiGetAllProductFeaturesByProductIdActionType = z.infer<
  typeof StripeApiGetAllProductFeaturesByProductIdActionSchema
>;

export const StripeApiGetAllCouponsActionSchema = z.object({
  action: z.literal("getAllCoupons"),
  orgs: OrgShopIdDtoSchema.array(),
});

export type StripeApiGetAllCouponsActionType = z.infer<
  typeof StripeApiGetAllCouponsActionSchema
>;

export const StripeApiGetPricesByProductIdActionSchema = z.object({
  action: z.literal("getPricesByProductId"),
  orgs: OrgShopIdDtoSchema.array(),
  productId: z.string(),
});

export type StripeApiGetPricesByProductIdActionType = z.infer<
  typeof StripeApiGetPricesByProductIdActionSchema
>;

export const StripeApiGetSubscriptionByIdActionSchema = z.object({
  action: z.literal("getSubscriptionById"),
  orgs: OrgShopIdDtoSchema.array(),
  stripeSubscriptionId: z.string(),
  subscriptionLinkIds: z.string().array(),
});

export type StripeApiGetSubscriptionByIdActionType = z.infer<
  typeof StripeApiGetSubscriptionByIdActionSchema
>;

export const StripeApiGetAllSubscriptionLinksByOrgIdActionSchema = z.object({
  action: z.literal("getAllSubscriptionLinksByOrgId"),
  orgs: OrgShopIdDtoSchema.array(),
});

export type StripeApiGetAllSubscriptionLinksByOrgIdActionType = z.infer<
  typeof StripeApiGetAllSubscriptionLinksByOrgIdActionSchema
>;

export const StripeApiGetAllSubscriptionLinksByOrgIdResponseBodySchema =
  z.object({
    action: z.literal("getAllSubscriptionLinksByOrgId"),
    stripeSubscriptionLinks: StripeApiSubscriptionLinkDtoSchema.array(),
  });

export type StripeApiGetAllSubscriptionLinksByOrgIdResponseBodyType = z.infer<
  typeof StripeApiGetAllSubscriptionLinksByOrgIdResponseBodySchema
>;

export const StripeApiModifySubscriptionByIdActionSchema = z.object({
  action: z.literal("modifySubscriptionById"),
  orgs: OrgShopIdDtoSchema.array(),
  stripeSubscriptionId: z.string(),
  subscriptionLinkIds: z.string().array(),
  customerId: z.string(),
  phases: StripeApiCreateSubscriptionSchedulePhaseDtoSchema.array(),
  settings: StripeApiCreateSubscriptionSettingDtoSchema,
});

export type StripeApiModifySubscriptionByIdActionType = z.infer<
  typeof StripeApiModifySubscriptionByIdActionSchema
>;

export const StripeApiCreateCustomerDtoSchema = StripeApiCustomerDtoSchema.omit(
  { id: true, billing: true }
);

export const StripeApiCreateCustomerActionSchema = z.object({
  action: z.literal("createCustomer"),
  orgs: OrgShopIdDtoSchema.array(),
  customer: StripeApiCreateCustomerDtoSchema,
});

export const StripeApiCreateCouponActionSchema = z.object({
  action: z.literal("createCoupon"),
  orgs: OrgShopIdDtoSchema.array(),
  coupon: StripeApiCreateCouponDtoSchema,
});

export type StripeApiCreateCouponActionType = z.infer<
  typeof StripeApiCreateCouponActionSchema
>;

export type StripeApiCreateCustomerActionType = z.infer<
  typeof StripeApiCreateCustomerActionSchema
>;

export const StripeApiAddPhaseToSubscriptionBySubscriptionIdActionSchema =
  z.object({
    action: z.literal("addPhaseToSubscriptionBySubscriptionId"),
    orgs: OrgShopIdDtoSchema.array(),
    stripeSubscriptionId: z.string(),
    subscriptionLinkId: z.string(),
    couponId: z.string().nullish(),
    items: StripeApiSubscriptionLineItemSchema.array(),
  });

export type StripeApiAddPhaseToSubscriptionBySubscriptionIdActionType = z.infer<
  typeof StripeApiAddPhaseToSubscriptionBySubscriptionIdActionSchema
>;

export const StripeApiAddPhaseToSubscriptionBySubscriptionIdResponseBodySchema =
  z.object({
    action: z.literal("addPhaseToSubscriptionBySubscriptionId"),
    subscription: z.object({
      id: z.string(),
    }),
    subscriptionSchedule: z.object({
      id: z.string(),
    }),
  });

export type StripeApiAddPhaseToSubscriptionBySubscriptionIdResponseBodyType =
  z.infer<
    typeof StripeApiAddPhaseToSubscriptionBySubscriptionIdResponseBodySchema
  >;

export const StripeApiCancelSubscriptionActionSchema = z.object({
  action: z.literal("cancelSubscriptionById"),
  orgs: OrgShopIdDtoSchema.array(),
  stripeSubscriptionId: z.string(),
});
export type StripeApiCancelSubscriptionActionType = z.infer<
  typeof StripeApiCancelSubscriptionActionSchema
>;

export const StripeApiPauseSubscriptionActionSchema = z.object({
  action: z.literal("pauseSubscriptionById"),
  orgs: OrgShopIdDtoSchema.array(),
  stripeSubscriptionId: z.string(),
});
export type StripeApiPauseSubscriptionActionType = z.infer<
  typeof StripeApiPauseSubscriptionActionSchema
>;

export const StripeApiCancelSubscriptionResponseBodySchema = z.object({
  action: z.literal("cancelSubscriptionById"),
  subscription: z.object({
    id: z.string(),
  }),
});
export type StripeApiCancelSubscriptionResponseBodyType = z.infer<
  typeof StripeApiCancelSubscriptionResponseBodySchema
>;

export const StripeApiGetAllCouponsResponseBodySchema = z.object({
  action: z.literal("getAllCoupons"),
  coupons: StripeApiCouponDtoSchema.array(),
});

export type StripeApiGetAllCouponsResponseBodyType = z.infer<
  typeof StripeApiGetAllCouponsResponseBodySchema
>;
export const StripeApiCreateCouponResponseBodySchema = z.object({
  action: z.literal("createCoupon"),
  coupon: StripeApiCouponDtoSchema,
});

export const StripeApiModifySubscriptionByIdResponseBodySchema = z.object({
  action: z.literal("modifySubscriptionById"),
  subscription: z.object({ id: z.string() }),
});

export type StripeApiModifySubscriptionByIdResponseBodyType = z.infer<
  typeof StripeApiModifySubscriptionByIdResponseBodySchema
>;

export type StripeApiCreateCouponResponseBodyType = z.infer<
  typeof StripeApiCreateCouponResponseBodySchema
>;

export const StripeApiCreateCustomerResponseBodySchema = z.object({
  action: z.literal("createCustomer"),
  customer: StripeApiCustomerDtoSchema,
});
export type StripeApiCreateCustomerResponseBodyType = z.infer<
  typeof StripeApiCreateCustomerResponseBodySchema
>;

export const StripeApiConvertToFlexibleResponseBodySchema = z.object({
  action: z.literal("convertToFlexible"),
  subscription: z.object({
    id: z.string(),
  }),
});

export type StripeApiConvertToFlexibleResponseBodyType = z.infer<
  typeof StripeApiConvertToFlexibleResponseBodySchema
>;

export const StripeApiConvertToFlexibleActionSchema = z.object({
  action: z.literal("convertToFlexible"),
  orgs: OrgShopIdDtoSchema.array(),
  stripeSubscriptionId: z.string(),
});

export const StripeApiActionSchema = z.discriminatedUnion("action", [
  StripeApiGetAllCustomersActionSchema,
  StripeApiGetAllProductsActionSchema,
  StripeApiCreateCustomerActionSchema,
  StripeApiGetPricesByProductIdActionSchema,
  StripeApiGetAllProductFeaturesByProductIdActionSchema,
  StripeApiCreateSubscriptionActionSchema,
  StripeApiGetAllCouponsActionSchema,
  StripeApiCreateCouponActionSchema,
  StripeApiGetSubscriptionByIdActionSchema,
  StripeApiModifySubscriptionByIdActionSchema,
  StripeApiGetAllSubscriptionLinksByOrgIdActionSchema,
  StripeApiAddPhaseToSubscriptionBySubscriptionIdActionSchema,
  StripeApiCancelSubscriptionActionSchema,
  StripeApiConvertToFlexibleActionSchema,
]);

export const StripeApiResponseBodySchema = z.discriminatedUnion("action", [
  StripeApiGetAllCustomersResponseBodySchema,
  StripeApiGetAllProductsResponseBodySchema,
  StripeApiCreateCustomerResponseBodySchema,
  StripeApiGetAllPricesByProductIdResponseBodySchema,
  StripeApiGetAllProductFeaturesByProductIdResponseBodySchema,
  StripeApiCreateSubscriptionResponseBodySchema,
  StripeApiGetAllCouponsResponseBodySchema,
  StripeApiGetSubscriptionByIdResponseBodySchema,
  StripeApiCreateCouponResponseBodySchema,
  StripeApiModifySubscriptionByIdResponseBodySchema,
  StripeApiGetAllSubscriptionLinksByOrgIdResponseBodySchema,
  StripeApiAddPhaseToSubscriptionBySubscriptionIdResponseBodySchema,
  StripeApiCancelSubscriptionResponseBodySchema,
  StripeApiConvertToFlexibleResponseBodySchema,
]);

export const StripeApiRequestBodySchema = StripeApiActionSchema;

export type StripeApiRequestBodyType = z.infer<
  typeof StripeApiRequestBodySchema
>;
